import React from 'react';
import './SelectionInfoDisplay.css';

interface SelectionCounts {
  total: number;
  nodes: number;
  beams: number;
  supports: number;
  loads: number;
}

interface SelectionInfoDisplayProps {
  selectedNodeIds: string[];
  selectedBeamIds: string[];
  supports?: any[];
  loads?: any[];
  selectedNodesForPerp?: string[];
  selectedBeamForPerp?: string | null;
  className?: string;
}

export const SelectionInfoDisplay: React.FC<SelectionInfoDisplayProps> = ({
  selectedNodeIds = [],
  selectedBeamIds = [],
  supports = [],
  loads = [],
  selectedNodesForPerp = [],
  selectedBeamForPerp = null,
  className = ''
}) => {
  // Calculate selection counts including perpendicular mode selections
  const perpModeNodeCount = selectedNodesForPerp?.length || 0;
  const perpModeBeamCount = selectedBeamForPerp ? 1 : 0;
  
  const counts: SelectionCounts = {
    total: selectedNodeIds.length + selectedBeamIds.length + perpModeNodeCount + perpModeBeamCount,
    nodes: selectedNodeIds.length + perpModeNodeCount,
    beams: selectedBeamIds.length + perpModeBeamCount,
    supports: supports?.filter(s => s.isSelected)?.length || 0,
    loads: loads?.filter(l => l.isSelected)?.length || 0
  };

  // Debug logging to verify the component is receiving data
  React.useEffect(() => {
    if (counts.total > 0) {
      console.log('📊 SelectionInfoDisplay updated:', {
        nodes: selectedNodeIds.length,
        beams: selectedBeamIds.length,
        perpNodes: perpModeNodeCount,
        perpBeam: perpModeBeamCount,
        total: counts.total
      });
    }
  }, [counts.total, selectedNodeIds.length, selectedBeamIds.length, perpModeNodeCount, perpModeBeamCount]);

  // Don't render if nothing is selected
  if (counts.total === 0) {
    return null;
  }

  // Format display text based on selection
  const formatSelectionText = (): string => {
    const parts: string[] = [];
    
    if (counts.nodes > 0) parts.push(`${counts.nodes}N`);
    if (counts.beams > 0) parts.push(`${counts.beams}B`);
    if (counts.supports > 0) parts.push(`${counts.supports}S`);
    if (counts.loads > 0) parts.push(`${counts.loads}L`);
    
    // Add special indicator for perpendicular mode
    if ((selectedNodesForPerp && selectedNodesForPerp.length > 0) || selectedBeamForPerp) {
      return parts.length > 0 ? `${parts.join(' ')} ⊥` : '⊥ Mode';
    }
    
    return parts.length > 0 ? parts.join(' ') : `${counts.total} items`;
  };

  // Generate comprehensive tooltip
  const generateTooltip = (): string => {
    const tooltip = [`Selected: ${counts.nodes} nodes, ${counts.beams} beams`];
    
    if (counts.supports > 0) tooltip.push(`${counts.supports} supports`);
    if (counts.loads > 0) tooltip.push(`${counts.loads} loads`);
    
    if (selectedBeamForPerp) {
      tooltip.push(`Reference beam: ${selectedBeamForPerp}`);
    }
    if (selectedNodesForPerp && selectedNodesForPerp.length > 0) {
      tooltip.push(`Target nodes: ${selectedNodesForPerp.length}`);
    }
    
    return tooltip.join(', ');
  };

  return (
    <div 
      className={`selection-info bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 shadow-sm ${className}`}
      style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        width: '120px',
        height: '30px',
        fontSize: '12px',
        padding: '5px 8px',
        borderRadius: '4px',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        
        // Semi-transparent and non-intrusive
        opacity: 0.9,
        transition: 'opacity 0.2s ease-in-out',
        pointerEvents: 'none', // Don't interfere with 3D interaction
        
        // Font styling
        fontFamily: 'ui-sans-serif, system-ui, sans-serif'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.opacity = '1';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.opacity = '0.9';
      }}
      title={generateTooltip()}
    >
      <span className="flex items-center">
        <span className="mr-1">🔲</span>
        <span className="font-medium">
          {formatSelectionText()}
        </span>
      </span>
    </div>
  );
};
